import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bell, BellOff, Satellite, Star, Moon, Sun, 
  Calendar, Clock, MapPin, ChevronRight, X,
  Sparkles, AlertTriangle, Check, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Upcoming celestial events data
const CELESTIAL_EVENTS = [
  {
    id: 'quadrantids_2026',
    type: 'meteor_shower',
    name: 'Quadrantids Meteor Shower',
    peak: '2026-01-20T06:00:00Z',
    description: 'One of the best annual meteor showers. Up to 120 meteors per hour at peak.',
    intensity: 'high',
    icon: Sparkles,
    color: '#ffd700'
  },
  {
    id: 'wolf_moon_2026',
    type: 'full_moon',
    name: 'Wolf Moon (Full Moon)',
    peak: '2026-01-25T22:27:00Z',
    description: 'First full moon of 2026. Named after howling wolves in winter.',
    intensity: 'medium',
    icon: Moon,
    color: '#e0e0e0'
  },
  {
    id: 'venus_mars_2026',
    type: 'conjunction',
    name: 'Venus-Mars Conjunction',
    peak: '2026-02-05T05:00:00Z',
    description: 'Venus and Mars appear very close together in the evening sky.',
    intensity: 'high',
    icon: Star,
    color: '#ffd27f'
  },
  {
    id: 'lyrids_2026',
    type: 'meteor_shower',
    name: 'Lyrids Meteor Shower',
    peak: '2026-04-22T04:00:00Z',
    description: 'Annual meteor shower producing up to 20 meteors per hour.',
    intensity: 'medium',
    icon: Sparkles,
    color: '#00f2ea'
  },
  {
    id: 'eta_aquarids_2026',
    type: 'meteor_shower',
    name: 'Eta Aquarids Meteor Shower',
    peak: '2026-05-06T03:00:00Z',
    description: 'Debris from Halley\'s Comet. Up to 60 meteors per hour.',
    intensity: 'high',
    icon: Sparkles,
    color: '#a855f7'
  },
  {
    id: 'perseids_2026',
    type: 'meteor_shower',
    name: 'Perseids Meteor Shower',
    peak: '2026-08-12T04:00:00Z',
    description: 'Most popular meteor shower. Up to 100 meteors per hour at peak.',
    intensity: 'very_high',
    icon: Sparkles,
    color: '#ff6b4a'
  },
  {
    id: 'lunar_eclipse_2026',
    type: 'eclipse',
    name: 'Total Lunar Eclipse',
    peak: '2026-03-03T06:58:00Z',
    description: 'Total lunar eclipse visible from Americas, Europe, and Africa.',
    intensity: 'very_high',
    icon: Moon,
    color: '#ff4444'
  },
  {
    id: 'geminids_2026',
    type: 'meteor_shower',
    name: 'Geminids Meteor Shower',
    peak: '2026-12-14T02:00:00Z',
    description: 'King of meteor showers. Up to 150 multicolored meteors per hour.',
    intensity: 'very_high',
    icon: Sparkles,
    color: '#22c55e'
  }
];

// ISS Pass prediction (simulated - in production would use NASA API)
const generateISSPasses = (lat, lon) => {
  const passes = [];
  const now = new Date();
  
  for (let i = 0; i < 5; i++) {
    const passTime = new Date(now.getTime() + (i + 1) * 12 * 60 * 60 * 1000 + Math.random() * 6 * 60 * 60 * 1000);
    const duration = Math.floor(Math.random() * 4 + 2); // 2-6 minutes
    const maxElevation = Math.floor(Math.random() * 60 + 20); // 20-80 degrees
    const brightness = -(Math.random() * 2 + 1).toFixed(1); // -1 to -3 magnitude
    
    passes.push({
      id: `iss_${i}`,
      type: 'iss_pass',
      name: 'ISS Pass',
      time: passTime.toISOString(),
      duration: `${duration} min`,
      maxElevation: `${maxElevation}°`,
      brightness: `${brightness} mag`,
      direction: ['NW to SE', 'SW to NE', 'W to E', 'NW to E'][Math.floor(Math.random() * 4)],
      visible: maxElevation > 40
    });
  }
  
  return passes;
};

// Request notification permission
const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    return false;
  }
  
  if (Notification.permission === 'granted') {
    return true;
  }
  
  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }
  
  return false;
};

// Send notification
const sendNotification = (title, body, icon = '/favicon.ico') => {
  if (Notification.permission === 'granted') {
    new Notification(title, {
      body,
      icon,
      badge: '/favicon.ico',
      vibrate: [200, 100, 200],
      tag: 'skybot-alert'
    });
  }
};

// Schedule notification
const scheduleNotification = (event, minutesBefore = 30) => {
  const eventTime = new Date(event.peak || event.time);
  const notifyTime = new Date(eventTime.getTime() - minutesBefore * 60 * 1000);
  const now = new Date();
  
  if (notifyTime > now) {
    const delay = notifyTime.getTime() - now.getTime();
    
    setTimeout(() => {
      sendNotification(
        `🌟 ${event.name} in ${minutesBefore} minutes!`,
        event.description,
        '/favicon.ico'
      );
    }, delay);
    
    return true;
  }
  
  return false;
};

// Night Sky Alerts Component
const NightSkyAlerts = ({ isOpen, onClose }) => {
  const { theme } = useTheme();
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [userLocation, setUserLocation] = useState({ lat: 40.7128, lon: -74.0060 });
  const [issPasses, setIssPasses] = useState([]);
  const [subscribedEvents, setSubscribedEvents] = useState([]);
  const [activeTab, setActiveTab] = useState('events'); // 'events', 'iss', 'settings'
  const [loading, setLoading] = useState(false);

  // Check notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
    
    // Load subscribed events from localStorage
    const saved = localStorage.getItem('skybot_subscribed_events');
    if (saved) {
      setSubscribedEvents(JSON.parse(saved));
    }
  }, []);

  // Get user location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lon: position.coords.longitude
          });
        },
        () => {} // Ignore errors, use default
      );
    }
  }, []);

  // Generate ISS passes when location changes
  useEffect(() => {
    setIssPasses(generateISSPasses(userLocation.lat, userLocation.lon));
  }, [userLocation]);

  // Enable notifications
  const handleEnableNotifications = async () => {
    const granted = await requestNotificationPermission();
    setNotificationsEnabled(granted);
    
    if (granted) {
      toast.success('🔔 Notifications enabled!');
      // Send test notification
      setTimeout(() => {
        sendNotification('Skybot Alerts Active', 'You\'ll be notified about celestial events!');
      }, 1000);
    } else {
      toast.error('Notification permission denied');
    }
  };

  // Subscribe to event
  const toggleEventSubscription = (eventId) => {
    let newSubscribed;
    
    if (subscribedEvents.includes(eventId)) {
      newSubscribed = subscribedEvents.filter(id => id !== eventId);
      toast.info('Unsubscribed from event');
    } else {
      newSubscribed = [...subscribedEvents, eventId];
      
      // Schedule notification for event
      const event = CELESTIAL_EVENTS.find(e => e.id === eventId);
      if (event && notificationsEnabled) {
        scheduleNotification(event, 60); // 1 hour before
        scheduleNotification(event, 30); // 30 min before
        toast.success(`🔔 Alert set for ${event.name}`);
      }
    }
    
    setSubscribedEvents(newSubscribed);
    localStorage.setItem('skybot_subscribed_events', JSON.stringify(newSubscribed));
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get time until event
  const getTimeUntil = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    
    if (diff < 0) return 'Past';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h`;
    
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${minutes}m`;
  };

  // Get upcoming events (future only)
  const upcomingEvents = CELESTIAL_EVENTS.filter(e => new Date(e.peak) > new Date())
    .sort((a, b) => new Date(a.peak) - new Date(b.peak));

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: 'rgba(0,0,0,0.9)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative w-full max-w-lg max-h-[90vh] rounded-2xl overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, #0a1020 0%, #02040a 100%)',
            border: `1px solid ${theme.colors.primary}30`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div 
            className="flex items-center justify-between p-4 border-b"
            style={{ borderColor: `${theme.colors.primary}20` }}
          >
            <div className="flex items-center gap-3">
              <div 
                className="p-2 rounded-xl"
                style={{ background: `${theme.colors.primary}20` }}
              >
                <Bell className="w-5 h-5" style={{ color: theme.colors.primary }} />
              </div>
              <div>
                <h2 className="font-unbounded font-bold text-lg text-white">Night Sky Alerts</h2>
                <p className="text-xs text-slate-500">Never miss a celestial event</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-white/10 transition-all"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          {/* Notification Banner */}
          {!notificationsEnabled && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mx-4 mt-4 p-3 rounded-xl"
              style={{
                background: 'linear-gradient(135deg, rgba(255,215,0,0.15), rgba(255,136,0,0.1))',
                border: '1px solid rgba(255,215,0,0.3)'
              }}
            >
              <div className="flex items-center gap-3">
                <BellOff className="w-5 h-5 text-[#ffd700]" />
                <div className="flex-1">
                  <p className="text-sm text-white font-medium">Enable Notifications</p>
                  <p className="text-xs text-slate-400">Get alerts for ISS passes & meteor showers</p>
                </div>
                <button
                  onClick={handleEnableNotifications}
                  className="px-3 py-1.5 rounded-xl text-xs font-bold"
                  style={{
                    background: 'linear-gradient(135deg, #ffd700, #ff8800)',
                    color: '#000'
                  }}
                >
                  Enable
                </button>
              </div>
            </motion.div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 p-4 pb-2">
            {[
              { id: 'events', label: 'Events', icon: Calendar },
              { id: 'iss', label: 'ISS Passes', icon: Satellite },
              { id: 'settings', label: 'Settings', icon: Settings }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-bold transition-all ${
                  activeTab === tab.id ? 'bg-white/10' : 'bg-transparent'
                }`}
                style={{
                  color: activeTab === tab.id ? theme.colors.primary : '#888',
                  border: activeTab === tab.id ? `1px solid ${theme.colors.primary}40` : '1px solid transparent'
                }}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="p-4 pt-2 overflow-y-auto" style={{ minHeight: '350px', maxHeight: '60vh' }}>
            {/* Events Tab */}
            {activeTab === 'events' && (
              <div className="space-y-3">
                {upcomingEvents.map((event) => (
                  <motion.div
                    key={event.id}
                    whileHover={{ scale: 1.01 }}
                    className="p-4 rounded-xl transition-all cursor-pointer"
                    style={{
                      background: subscribedEvents.includes(event.id) 
                        ? `${event.color}15` 
                        : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${subscribedEvents.includes(event.id) ? event.color + '50' : 'rgba(255,255,255,0.1)'}`
                    }}
                    onClick={() => toggleEventSubscription(event.id)}
                  >
                    <div className="flex items-start gap-3">
                      <div 
                        className="p-2 rounded-xl"
                        style={{ background: `${event.color}20` }}
                      >
                        <event.icon className="w-5 h-5" style={{ color: event.color }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="font-bold text-white text-sm">{event.name}</h3>
                          <span 
                            className="text-xs font-mono px-2 py-0.5 rounded-full"
                            style={{ 
                              background: `${event.color}20`,
                              color: event.color
                            }}
                          >
                            {getTimeUntil(event.peak)}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">{event.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Clock className="w-3 h-3 text-slate-500" />
                          <span className="text-xs text-slate-500">{formatDate(event.peak)}</span>
                        </div>
                      </div>
                      <div className="flex items-center">
                        {subscribedEvents.includes(event.id) ? (
                          <div 
                            className="p-1.5 rounded-full"
                            style={{ background: `${theme.colors.primary}30` }}
                          >
                            <Bell className="w-4 h-4" style={{ color: theme.colors.primary }} />
                          </div>
                        ) : (
                          <div className="p-1.5 rounded-full bg-white/5">
                            <BellOff className="w-4 h-4 text-slate-600" />
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* ISS Tab */}
            {activeTab === 'iss' && (
              <div className="space-y-3">
                <div 
                  className="p-3 rounded-xl mb-4"
                  style={{ background: 'rgba(255,255,255,0.03)' }}
                >
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <MapPin className="w-3 h-3" />
                    <span>Passes for {userLocation.lat.toFixed(2)}°, {userLocation.lon.toFixed(2)}°</span>
                  </div>
                </div>

                {issPasses.map((pass) => (
                  <motion.div
                    key={pass.id}
                    whileHover={{ scale: 1.01 }}
                    className="p-4 rounded-xl transition-all"
                    style={{
                      background: pass.visible ? 'rgba(34,197,94,0.1)' : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${pass.visible ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.1)'}`
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div 
                        className="p-2 rounded-xl"
                        style={{ background: pass.visible ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.1)' }}
                      >
                        <Satellite className="w-5 h-5" style={{ color: pass.visible ? '#22c55e' : '#888' }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="font-bold text-white text-sm">
                            ISS Pass {pass.visible && <span className="text-[#22c55e] text-xs ml-2">VISIBLE</span>}
                          </h3>
                          <span className="text-xs font-mono text-slate-400">
                            {getTimeUntil(pass.time)}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <div className="text-xs text-slate-400">
                            <span className="text-slate-600">Duration:</span> {pass.duration}
                          </div>
                          <div className="text-xs text-slate-400">
                            <span className="text-slate-600">Max Alt:</span> {pass.maxElevation}
                          </div>
                          <div className="text-xs text-slate-400">
                            <span className="text-slate-600">Brightness:</span> {pass.brightness}
                          </div>
                          <div className="text-xs text-slate-400">
                            <span className="text-slate-600">Direction:</span> {pass.direction}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <Clock className="w-3 h-3 text-slate-500" />
                          <span className="text-xs text-slate-500">{formatDate(pass.time)}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="space-y-4">
                <div 
                  className="p-4 rounded-xl"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Bell className="w-5 h-5" style={{ color: theme.colors.primary }} />
                      <div>
                        <p className="text-sm text-white font-medium">Push Notifications</p>
                        <p className="text-xs text-slate-500">Receive alerts on your device</p>
                      </div>
                    </div>
                    <div 
                      className={`w-12 h-6 rounded-full cursor-pointer transition-all ${
                        notificationsEnabled ? 'bg-green-500' : 'bg-slate-700'
                      }`}
                      onClick={notificationsEnabled ? () => {} : handleEnableNotifications}
                    >
                      <div 
                        className={`w-5 h-5 rounded-full bg-white shadow-md transform transition-all ${
                          notificationsEnabled ? 'translate-x-6' : 'translate-x-0.5'
                        } mt-0.5`}
                      />
                    </div>
                  </div>
                </div>

                <div 
                  className="p-4 rounded-xl"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  <h3 className="text-sm text-white font-medium mb-3">Alert Timing</h3>
                  <div className="space-y-2">
                    {['1 hour before', '30 minutes before', '15 minutes before'].map((timing, i) => (
                      <label key={timing} className="flex items-center gap-3 cursor-pointer">
                        <input 
                          type="checkbox" 
                          defaultChecked={i < 2}
                          className="w-4 h-4 rounded accent-cyan-500"
                        />
                        <span className="text-sm text-slate-400">{timing}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div 
                  className="p-4 rounded-xl"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  <h3 className="text-sm text-white font-medium mb-3">Event Types</h3>
                  <div className="space-y-2">
                    {[
                      { label: 'Meteor Showers', icon: Sparkles, color: '#ffd700' },
                      { label: 'ISS Passes', icon: Satellite, color: '#22c55e' },
                      { label: 'Lunar Events', icon: Moon, color: '#e0e0e0' },
                      { label: 'Planetary Alignments', icon: Star, color: '#a855f7' }
                    ].map((type) => (
                      <label key={type.label} className="flex items-center gap-3 cursor-pointer">
                        <input 
                          type="checkbox" 
                          defaultChecked
                          className="w-4 h-4 rounded accent-cyan-500"
                        />
                        <type.icon className="w-4 h-4" style={{ color: type.color }} />
                        <span className="text-sm text-slate-400">{type.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <p className="text-xs text-slate-600 text-center">
                  Subscribed to {subscribedEvents.length} events
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NightSkyAlerts;
